import { useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/shared/hooks/use-toast';
import { TrainingPartnerFeedback, FeedbackFormData, FeedbackResponse } from '@/types/feedback';

interface UsePartnerFeedbackProps {
  trainingId?: string;
}

export const usePartnerFeedback = ({ trainingId }: UsePartnerFeedbackProps = {}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedbacks, setFeedbacks] = useState<TrainingPartnerFeedback[]>([]);
  const { user, session } = useAuth();
  const { toast } = useToast();

  const saveFeedback = useCallback(async (feedbackData: FeedbackFormData): Promise<boolean> => {
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to save feedback",
        variant: "destructive",
      });
      return false;
    }

    setSaving(true);
    try {
      // Use local Supabase functions URL in development
      const baseUrl = import.meta.env.DEV
        ? 'http://127.0.0.1:54321'
        : import.meta.env.VITE_SUPABASE_URL;

      const response = await fetch(`${baseUrl}/functions/v1/save_partner_feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify(feedbackData)
      });

      const result: FeedbackResponse = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to save feedback');
      }

      if (result.success) {
        toast({
          title: "Success",
          description: "Feedback saved successfully!",
        });
        return true;
      } else {
        throw new Error(result.error || 'Failed to save feedback');
      }
    } catch (error) {
      console.error('Error saving feedback:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save feedback",
        variant: "destructive",
      });
      return false;
    } finally {
      setSaving(false);
    }
  }, [user, toast, session?.access_token]);

  const getFeedback = useCallback(async (trainingId: string, praisedId: string): Promise<TrainingPartnerFeedback | null> => {
    if (!user) {
      return null;
    }

    setLoading(true);
    try {
      // Use local Supabase functions URL in development
      const baseUrl = import.meta.env.DEV
        ? 'http://127.0.0.1:54321'
        : import.meta.env.VITE_SUPABASE_URL;

      const response = await fetch(`${baseUrl}/functions/v1/get_partner_feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ training_id: trainingId, praised_id: praisedId })
      });

      const result: FeedbackResponse = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to get feedback');
      }

      return result.feedback || null;
    } catch (error) {
      console.error('Error getting feedback:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [user, session?.access_token]);

  const getTrainingFeedbacks = useCallback(async (trainingId: string): Promise<TrainingPartnerFeedback[]> => {
    if (!user) {
      return [];
    }

    if (!session) {
      return [];
    }

    setLoading(true);
    try {
      // Use local Supabase functions URL in development
      const baseUrl = import.meta.env.DEV
        ? 'http://127.0.0.1:54321'
        : import.meta.env.VITE_SUPABASE_URL;

      const response = await fetch(`${baseUrl}/functions/v1/get_training_feedbacks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ training_notes_id: trainingId }) // Changed from training_id to training_notes_id
      });

      const result: FeedbackResponse = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to get training feedbacks');
      }

      const feedbacks = result.feedbacks || [];
      setFeedbacks(feedbacks);
      return feedbacks;
    } catch (error) {
      console.error('Error getting training feedbacks:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, [user, session]);

  const hasFeedback = useCallback((praisedId: string): boolean => {
    return feedbacks.some(feedback => feedback.praised_id === praisedId);
  }, [feedbacks]);

  const getFeedbackForPartner = useCallback((praisedId: string): TrainingPartnerFeedback | null => {
    return feedbacks.find(feedback => feedback.praised_id === praisedId) || null;
  }, [feedbacks]);

  const clearFeedbacks = useCallback(() => {
    setFeedbacks([]);
  }, []);

  return {
    loading,
    saving,
    feedbacks,
    saveFeedback,
    getFeedback,
    getTrainingFeedbacks,
    hasFeedback,
    getFeedbackForPartner,
    clearFeedbacks,
  };
}; 